import { ReactNode, useContext, useEffect } from 'react';
import { AppContext } from '@/contexts/AppContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';
import useRole from '@/personas/useRole';
import RoleSelectScreen from '@/pages/Role/RoleSelectScreen';

type RoleGateProps = {
  children?: ReactNode;
};

/**
 * Grind mountad i AuthGuard, INUTI SupervisorScene (så den ser context) men FÖRE
 * AuthRouter (så rollvalet är avgjort innan någon route/SuperviseGuard körs).
 *
 * - role === null  → rendera rollväljaren (RoleSelectScreen), UTOM för brukarkonton
 *                    (se skipRoleSelection nedan), som släpps rakt igenom.
 * - role !== null  → synka isSupervising till det persisterade valet i en
 *                    useEffect (ALDRIG under render — annars dispatchas
 *                    'storage' mitt i en render-fas, R1) och rendera children.
 *
 * Rollsanningen förblir isSupervising i SupervisorContext; RoleGate äger bara det
 * persisterade valet (app:role:<tag>) och håller context i synk med det.
 */
const RoleGate = ({ children }: RoleGateProps) => {
  const { role } = useRole();
  const { user } = useContext(AppContext);
  const { isSupervising, setSupervising, currentClient } = useContext(SupervisorContext);
  const effectiveChildMode = useEffectiveChildMode({ currentClient, isSupervising });

  const desiredSupervising = role === 'supervisor';

  useEffect(() => {
    if (role === null) return; // inget val ännu → rollväljaren visas, rör inte context
    if (isSupervising !== desiredSupervising) {
      setSupervising(desiredSupervising);
    }
  }, [role, desiredSupervising, isSupervising, setSupervising]);

  /**
   * Rollväljaren är en fråga till en VUXEN. Ingen befintlig användare har nyckeln
   * `app:role:<tag>`, så utan det här villkoret möts varenda konto av den vid
   * första start efter releasen — barnen inräknade. Ett barn som väljer "Stötta
   * någon" hamnar direkt i vuxengränssnittet, och vägen tillbaka går via
   * Inställningar och vuxentext (RoleSwitchSetting är dessutom dev-only). För
   * målgruppen är det i praktiken ett lås.
   *
   * Villkoret är "kontot är en brukare", uttryckt via de två källor som faktiskt
   * vet det:
   *   - effectiveChildMode: kontots NPF-flagga, med samma formel som resten av
   *     appen gatar barnvyn på (childMode && ingen vald brukare && inte handledare).
   *     En returnerande handledare har 'supervising' satt och faller därför INTE
   *     hit, även om barnläget råkar ligga kvar på enheten.
   *   - user.persona === 'child': serverns sanning, som överlever en ominstallation
   *     där localStorage är tomt och childMode-flaggan alltså saknas.
   *
   * "Avsaknad av handledarkoppling" valdes BORT som villkor: en handledare som
   * ännu inte kopplat sin första brukare har noll klienter, och att dölja
   * rollväljaren för dem hade låst ute handledaren i stället för barnet.
   *
   * Vi persisterar INTE något rollval här. Att tyst skriva `app:role:<tag>` vore
   * att lägga ett beslut i brukarens namn; att bara låta bli att fråga gör att
   * rollen förblir osatt (= brukarläge, isSupervising rörs inte) och att frågan
   * kommer tillbaka av sig själv om kontot senare inte längre är i barnläge.
   *
   * REGRESSIONSFIX (granskningsrunda 2): effectiveChildMode läser en ENHETS-
   * global flagga (localStorage `childMode`) som aldrig nollställs vid utloggning
   * eller kontobyte (se childMode.ts/useEffectiveChildMode.ts). Ett barn som
   * använt en delad enhet lämnar kvar childMode='on'; loggar sedan en RIKTIG
   * vuxen (persona 'adult') in på samma enhet — utan klienter, aldrig i
   * handledarläge — blev effectiveChildMode ändå true, och utan spärren nedan
   * hade rollväljaren ALDRIG visats för hen. Eftersom role då förblir null för
   * alltid (vi persisterar medvetet inget här) satt den vuxna fast i barnvyn
   * permanent, utan att någonsin ha blivit tillfrågad — exakt den felklass
   * villkoret ovan skulle förhindra, fast riktad mot en annan användare.
   *
   * isKnownAdult (server-sanning, INTE enhetsflaggan) vinner därför alltid över
   * effectiveChildMode: en känd vuxen får se rollväljaren oavsett vad enhetens
   * childMode råkar stå i. Handledarens etablerade väg ut ur en stale flagga —
   * välja "Stötta någon" → setSupervising(true) → effectiveChildMode blir false
   * INTE bara här utan överallt (ChildRouteSwitch, tema, m.fl.) — är opåverkad
   * och fungerar igen så fort rollväljaren väl visas.
   *
   * persona 'teen' är MEDVETET INTE undantaget här (till skillnad från Granskarens
   * förslag att lägga till teen i själva skip-villkoret, vilket är en annan sak
   * och inte beställt): PersonaSelector sätter childMode=true för BÅDE child och
   * teen, så en teen med childMode på enheten ska fortsätta hoppa förbi
   * rollväljaren precis som innan — det var aldrig trasigt och är inte del av
   * detta fynd.
   */
  // Strikt 'adult', INTE "allt som inte är child/teen". Granskningen ville vidga det
  // till att även räkna ett saknat persona-fält som vuxet, eftersom api/types/User.ts
  // säger att avsaknad betyder vuxen. Det avvisades, och skälet är att persona saknas
  // i exakt ETT läge: mot en äldre backend, alltså under utrullningen — och där saknas
  // det för BARNET lika mycket som för den vuxna. childMode='on' plus okänd persona är
  // samma tillstånd för båda, och den vidgade regeln hade därför visat rollväljaren för
  // varje barn som råkade öppna appen under deployfönstret, för att skydda en vuxen som
  // redan har en väg vidare ("Gå till föräldraläge" i inställningarna, som till skillnad
  // från barnvyns motsvarighet är synlig i produktion). Barnets skydd är permanent och
  // gäller alla; den vuxnas problem är tillfälligt och har en utväg.
  const isKnownAdult = user?.persona === 'adult';
  const skipRoleSelection = !isKnownAdult && (effectiveChildMode || user?.persona === 'child');

  if (role === null && !skipRoleSelection) return <RoleSelectScreen />;
  return <>{children}</>;
};

export default RoleGate;
